import React, { useState, useEffect, useMemo, useRef } from 'react';
import { View, Text, ActivityIndicator, ScrollView, Modal, Pressable, TextInput, StyleSheet, Platform, Alert } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { authApi } from '../../shared/utils/api';
import {
  DueDiligence,
  DueDiligenceItem,
  BasePropertyType,
  ModuleCode,
  WorkflowPhase,
  FinalDecision,
  ItemStatus,
  FlagType,
  getDefaultModules,
} from '../../shared/types/due-diligence';
import { ALL_MODULES } from '../../shared/due-diligence';
import {
  PropertyTypeSelector,
  ModuleSelector,
  PhaseIndicator,
  ChecklistBoard,
  FlagBadge,
} from './index';
import { Card, Button } from '../ui';
import { PageContainer } from '../layout';
import { PropertyLookup } from '../property';
import { Ionicons } from '@expo/vector-icons';

export interface DueDiligenceViewProps {
  portfolioId: number;
  propertyId: number;
  availablePartners: Array<{ id: number; email: string; display_name?: string }>;
}

export function DueDiligenceView({
  portfolioId,
  propertyId,
  availablePartners,
}: DueDiligenceViewProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile } = useResponsive();
  const viewStyles = makeViewStyles(tokens, utils, isMobile, isSmallMobile);

  // State
  const [dueDiligence, setDueDiligence] = useState<DueDiligence | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [setupModalVisible, setSetupModalVisible] = useState(false);
  const [decisionModalVisible, setDecisionModalVisible] = useState(false);

  // Setup form state
  const [selectedPropertyType, setSelectedPropertyType] = useState<BasePropertyType>('raw_land');
  const [selectedModules, setSelectedModules] = useState<ModuleCode[]>([]);
  
  // Ensure selectedModules is always an array
  const safeSelectedModules = selectedModules || [];
  const [decision, setDecision] = useState<FinalDecision | null>(null);
  const [decisionRationale, setDecisionRationale] = useState('');
  
  // Milestone tracking
  const previousCompletionRef = useRef<number | null>(null);
  const shownMilestonesRef = useRef<Set<number>>(new Set());
  
  // Milestone messages
  const MILESTONES: { threshold: number; title: string; message: string; emoji: string }[] = [
    { threshold: 2, title: 'Getting Started!', message: "You've taken the first steps. Every journey begins with a single checklist item!", emoji: '🌱' },
    { threshold: 5, title: 'Building Momentum!', message: "You're gathering valuable information. Keep it up!", emoji: '📋' },
    { threshold: 25, title: 'Quarter Complete!', message: "25% done! You're making real progress on your due diligence.", emoji: '🎯' },
    { threshold: 50, title: 'Halfway There!', message: "50% complete! You're halfway to a fully researched property.", emoji: '⭐' },
    { threshold: 75, title: 'Almost There!', message: "75% done! The finish line is in sight. Great work!", emoji: '🚀' },
    { threshold: 90, title: 'Final Stretch!', message: "90% complete! Just a few more items to review.", emoji: '🏁' },
    { threshold: 100, title: 'All Complete!', message: "100%! You've completed all due diligence items. Time to make a decision!", emoji: '🎉' },
  ];

  // Load due diligence data
  useEffect(() => {
    loadDueDiligence();
  }, [portfolioId, propertyId]);

  // Check for milestone achievements when completion changes
  useEffect(() => {
    if (!dueDiligence || loading) return;
    
    const currentCompletion = dueDiligence.completionPercent || 0;
    const previousCompletion = previousCompletionRef.current;
    
    // Skip if this is the first render or completion hasn't increased
    if (previousCompletion === null || currentCompletion <= previousCompletion) {
      previousCompletionRef.current = currentCompletion;
      return;
    }
    
    // Check which milestones were crossed
    for (const milestone of MILESTONES) {
      // Only show if we crossed this threshold AND haven't shown it before
      if (
        previousCompletion < milestone.threshold &&
        currentCompletion >= milestone.threshold &&
        !shownMilestonesRef.current.has(milestone.threshold)
      ) {
        shownMilestonesRef.current.add(milestone.threshold);
        // Show encouraging alert
        Alert.alert(
          `${milestone.emoji} ${milestone.title}`,
          milestone.message,
          [{ text: 'Keep Going!', style: 'default' }]
        );
        break; // Only show one milestone at a time
      }
    }
    
    previousCompletionRef.current = currentCompletion;
  }, [dueDiligence?.completionPercent, loading]);

  const loadDueDiligence = async () => {
    try {
      setLoading(true);
      const response = await authApi.getDueDiligence(portfolioId, propertyId);
      setDueDiligence(response.dueDiligence);
      
      // If no items exist, show setup modal
      if (response.dueDiligence && (!response.dueDiligence.items || response.dueDiligence.items.length === 0)) {
        setSetupModalVisible(true);
        setSelectedPropertyType(response.dueDiligence.basePropertyType);
        setSelectedModules(response.dueDiligence.enabledModules || []);
      }
    } catch (error: any) {
      console.error('Failed to load due diligence:', error);
      // If 404, show setup modal
      if (error.status === 404 || error.status === 500) {
        setSetupModalVisible(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    try {
      setInitializing(true);
      
      // Generate items from selected modules
      const itemsToCreate: any[] = [];
      for (const moduleCode of safeSelectedModules) {
        const module = ALL_MODULES[moduleCode];
        if (!module) continue;

        for (const category of module.categories) {
          for (const itemTemplate of category.items) {
            itemsToCreate.push({
              code: itemTemplate.code,
              module: moduleCode,
              category: category.code,
              title: itemTemplate.title,
              description: itemTemplate.description || null,
              isGroupTask: itemTemplate.isGroupTask || false,
            });
          }
        }
      }

      // Create/update configuration
      await authApi.createOrUpdateDueDiligence(portfolioId, propertyId, {
        basePropertyType: selectedPropertyType,
        enabledModules: safeSelectedModules,
      });

      // Initialize with items
      await authApi.initializeDueDiligence(portfolioId, propertyId, {
        basePropertyType: selectedPropertyType,
        enabledModules: safeSelectedModules,
        items: itemsToCreate,
      });

      setSetupModalVisible(false);
      await loadDueDiligence();
    } catch (error: any) {
      console.error('Failed to initialize due diligence:', error);
      alert(error.message || 'Failed to initialize due diligence');
    } finally {
      setInitializing(false);
    }
  };

  const handleUpdateItem = async (
    itemId: number,
    updates: {
      status?: ItemStatus;
      flag?: FlagType | null;
      notes?: string;
      dataFields?: Record<string, any>;
      assignedTo?: number[];
    }
  ) => {
    // Optimistic update - update local state immediately
    setDueDiligence(prev => {
      if (!prev || !prev.items) return prev;
      
      const updatedItems = prev.items.map(item => {
        if (item.id === itemId) {
          return {
            ...item,
            ...updates,
            // Handle null flag explicitly
            flag: updates.flag === null ? undefined : (updates.flag ?? item.flag),
          };
        }
        return item;
      });

      // Recalculate completion and flag summary (excluding N/A from percentage)
      const notApplicableCount = updatedItems.filter(i => i.status === 'not_applicable').length;
      const applicableItems = updatedItems.length - notApplicableCount;
      const completedItems = updatedItems.filter(i => i.status === 'complete').length;
      const completionPercent = applicableItems > 0 ? Math.round((completedItems / applicableItems) * 100) : 0;

      const flagSummary: DueDiligence['flagSummary'] = {
        big_red: updatedItems.filter(i => i.flag === 'big_red').length,
        red: updatedItems.filter(i => i.flag === 'red').length,
        yellow: updatedItems.filter(i => i.flag === 'yellow').length,
        green: updatedItems.filter(i => i.flag === 'green').length,
        big_green: updatedItems.filter(i => i.flag === 'big_green').length,
        clear: updatedItems.filter(i => i.flag === 'clear').length,
        pending: updatedItems.filter(i => !i.flag && i.status !== 'not_applicable').length,
        not_applicable: notApplicableCount,
      };

      return {
        ...prev,
        items: updatedItems,
        completionPercent,
        flagSummary,
      } as DueDiligence;
    });

    try {
      // Convert null flag to undefined for API
      const apiUpdates = {
        ...updates,
        flag: updates.flag === null ? undefined : updates.flag,
      };
      await authApi.updateDueDiligenceItem(portfolioId, propertyId, itemId, apiUpdates);
      // Don't re-fetch - we already updated locally
    } catch (error: any) {
      console.error('Failed to update item:', error);
      // On error, reload to get correct state
      await loadDueDiligence();
      throw error;
    }
  };

  const handleUpdatePhase = async (phase: WorkflowPhase) => {
    try {
      setSaving(true);
      await authApi.createOrUpdateDueDiligence(portfolioId, propertyId, {
        currentPhase: phase,
      });
      await loadDueDiligence();
    } catch (error: any) {
      console.error('Failed to update phase:', error);
      alert(error.message || 'Failed to update phase');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDecision = async () => {
    if (!decision) return;
    
    try {
      setSaving(true);
      await authApi.createOrUpdateDueDiligence(portfolioId, propertyId, {
        finalDecision: decision,
        decisionRationale: decisionRationale || undefined,
        currentPhase: 'decision',
      });
      setDecisionModalVisible(false);
      setDecisionRationale('');
      await loadDueDiligence();
    } catch (error: any) {
      console.error('Failed to save decision:', error);
      alert(error.message || 'Failed to save decision');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyLookupData = async (data: {
    property: any;
    dueDiligence: any;
  }) => {
    // Apply due diligence data to checklist items
    if (!dueDiligence?.items || !data.dueDiligence) return;

    try {
      setSaving(true);
      
      // Find items that match the lookup data and update them
      const updates: Promise<void>[] = [];
      
      for (const item of dueDiligence.items) {
        let dataToApply: Record<string, any> | null = null;
        
        // Match item codes to lookup data
        if (item.code === 'gps_coords' && data.dueDiligence.gps_coords) {
          dataToApply = data.dueDiligence.gps_coords;
        } else if (item.code === 'address_verify' && data.dueDiligence.address_verify) {
          dataToApply = data.dueDiligence.address_verify;
        } else if (item.code === 'jurisdiction' && data.dueDiligence.jurisdiction) {
          dataToApply = data.dueDiligence.jurisdiction;
        }
        
        if (dataToApply) {
          // Merge with existing data fields
          const mergedData = { ...item.dataFields, ...dataToApply };
          updates.push(
            authApi.updateDueDiligenceItem(portfolioId, propertyId, item.id, {
              dataFields: mergedData,
              status: 'in_progress' as ItemStatus, // Mark as in progress since we have data
            }).then(() => {})
          );
        }
      }
      
      if (updates.length > 0) {
        await Promise.all(updates);
        await loadDueDiligence();
        alert(`Updated ${updates.length} checklist items with lookup data!`);
      } else {
        alert('No matching checklist items found. Make sure you have Land Basics module enabled.');
      }
    } catch (error: any) {
      console.error('Failed to apply lookup data:', error);
      alert(error.message || 'Failed to apply data');
    } finally {
      setSaving(false);
    }
  };

  // Calculate summaries
  const flagSummary = useMemo(() => {
    if (!dueDiligence?.items) return null;
    return dueDiligence.flagSummary || {
      big_red: 0,
      red: 0,
      yellow: 0,
      green: 0,
      big_green: 0,
      clear: 0,
      pending: 0,
      not_applicable: 0,
    };
  }, [dueDiligence]);

  const statusSummary = useMemo(() => {
    if (!dueDiligence?.items) return null;
    return dueDiligence.statusSummary || {
      pending: 0,
      in_progress: 0,
      complete: 0,
      not_applicable: 0,
    };
  }, [dueDiligence]);

  if (loading) {
    return (
      <View style={viewStyles.loadingContainer}>
        <ActivityIndicator size="large" color={tokens.color.primary} />
        <Text style={viewStyles.loadingText}>Loading due diligence...</Text>
      </View>
    );
  }

  if (!dueDiligence) {
    return (
      <Card padding="lg" elevated>
        <View style={viewStyles.emptyState}>
          <Ionicons 
            name="document-text-outline" 
            size={48} 
            color={addColorOpacity(tokens.color.onSurface, 0.3)} 
          />
          <Text style={viewStyles.emptyText}>
            Due Diligence Not Started
          </Text>
          <Text style={viewStyles.emptySubtext}>
            Set up your due diligence checklist to begin evaluating this property
          </Text>
          <Button
            title="Start Due Diligence"
            onPress={() => setSetupModalVisible(true)}
            variant="primary"
            style={{ marginTop: utils.spacing.lg }}
          />
        </View>
      </Card>
    );
  }

  return (
    <View style={viewStyles.container}>
      {/* Phase Indicator */}
      {dueDiligence.currentPhase && (
        <Card padding="lg" elevated style={{ marginBottom: utils.spacing.lg }}>
          <PhaseIndicator
            currentPhase={dueDiligence.currentPhase}
            showDescriptions={!isMobile}
          />
        </Card>
      )}

      {/* Summary Cards */}
      {flagSummary && (
        <View style={viewStyles.summaryRow}>
          <Card padding="md" elevated style={viewStyles.summaryCard}>
            <Text style={viewStyles.summaryLabel}>Completion</Text>
            <Text style={viewStyles.summaryValue}>
              {dueDiligence.completionPercent || 0}%
            </Text>
          </Card>
          <Card padding="md" elevated style={viewStyles.summaryCard}>
            <Text style={viewStyles.summaryLabel}>Big Red</Text>
            <Text style={[viewStyles.summaryValue, { color: '#DC2626' }]}>
              {flagSummary.big_red}
            </Text>
          </Card>
          <Card padding="md" elevated style={viewStyles.summaryCard}>
            <Text style={viewStyles.summaryLabel}>Red</Text>
            <Text style={[viewStyles.summaryValue, { color: '#EF4444' }]}>
              {flagSummary.red}
            </Text>
          </Card>
          <Card padding="md" elevated style={viewStyles.summaryCard}>
            <Text style={viewStyles.summaryLabel}>Yellow</Text>
            <Text style={[viewStyles.summaryValue, { color: '#EAB308' }]}>
              {flagSummary.yellow}
            </Text>
          </Card>
          <Card padding="md" elevated style={viewStyles.summaryCard}>
            <Text style={viewStyles.summaryLabel}>Green</Text>
            <Text style={[viewStyles.summaryValue, { color: '#22C55E' }]}>
              {flagSummary.green}
            </Text>
          </Card>
          <Card padding="md" elevated style={viewStyles.summaryCard}>
            <Text style={viewStyles.summaryLabel}>Big Green</Text>
            <Text style={[viewStyles.summaryValue, { color: '#16A34A' }]}>
              {flagSummary.big_green}
            </Text>
          </Card>
          {flagSummary.not_applicable > 0 && (
            <Card padding="md" elevated style={viewStyles.summaryCard}>
              <Text style={viewStyles.summaryLabel}>N/A</Text>
              <Text style={[viewStyles.summaryValue, { color: tokens.color.muted }]}>
                {flagSummary.not_applicable}
              </Text>
            </Card>
          )}
        </View>
      )}

      {/* Action Buttons */}
      <View style={viewStyles.actionsRow}>
        {/* Property Lookup - Auto-fill address data */}
        <PropertyLookup
          onApplyData={handleApplyLookupData}
          variant="button"
          size="sm"
        />
        
        {dueDiligence.currentPhase && dueDiligence.currentPhase !== 'decision' && (
          <Button
            title="Update Phase"
            onPress={() => {
              // Simple phase progression
              const phases: WorkflowPhase[] = ['setup', 'initial_screen', 'deep_diligence', 'final_analysis', 'decision'];
              const currentIndex = dueDiligence.currentPhase ? phases.indexOf(dueDiligence.currentPhase) : 0;
              if (currentIndex >= 0 && currentIndex < phases.length - 1) {
                handleUpdatePhase(phases[currentIndex + 1]);
              }
            }}
            variant="outline"
            leftIcon="arrow-forward"
          />
        )}
        {dueDiligence.currentPhase === 'final_analysis' && !dueDiligence.finalDecision && (
          <Button
            title="Make Decision"
            onPress={() => setDecisionModalVisible(true)}
            variant="primary"
            leftIcon="checkmark-circle"
          />
        )}
        {dueDiligence.finalDecision && (
          <Card padding="md" elevated style={{ marginBottom: utils.spacing.lg }}>
            <View style={viewStyles.decisionCard}>
              <Text style={viewStyles.decisionLabel}>Final Decision</Text>
              <View style={viewStyles.decisionContent}>
                <Text style={[
                  viewStyles.decisionValue,
                  { color: dueDiligence.finalDecision === 'go' ? '#22C55E' : dueDiligence.finalDecision === 'no_go' ? '#EF4444' : '#F59E0B' }
                ]}>
                  {dueDiligence.finalDecision === 'go' ? 'Go' : dueDiligence.finalDecision === 'no_go' ? 'No Go' : 'Pass for Now'}
                </Text>
                {dueDiligence.decisionRationale && (
                  <Text style={viewStyles.decisionRationale}>
                    {dueDiligence.decisionRationale}
                  </Text>
                )}
              </View>
            </View>
          </Card>
        )}
      </View>

      {/* Checklist Board */}
      {dueDiligence.items && dueDiligence.items.length > 0 ? (
        <ChecklistBoard
          items={dueDiligence.items}
          portfolioId={portfolioId}
          propertyId={propertyId}
          onUpdateItem={handleUpdateItem}
          availablePartners={availablePartners}
          loading={saving}
          viewMode="by_module"
        />
      ) : (
        <Card padding="lg" elevated>
          <View style={viewStyles.emptyState}>
            <Ionicons 
              name="checkbox-outline" 
              size={48} 
              color={addColorOpacity(tokens.color.onSurface, 0.3)} 
            />
            <Text style={viewStyles.emptyText}>
              No checklist items yet
            </Text>
            <Button
              title="Initialize Checklist"
              onPress={() => setSetupModalVisible(true)}
              variant="primary"
              style={{ marginTop: utils.spacing.lg }}
            />
          </View>
        </Card>
      )}

      {/* Setup Modal */}
      <Modal
        visible={setupModalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setSetupModalVisible(false)}
      >
        <View style={viewStyles.modalOverlay}>
          <View style={viewStyles.modalContent}>
            <View style={viewStyles.modalHeader}>
              <Text style={viewStyles.modalTitle}>Setup Due Diligence</Text>
              <Pressable
                onPress={() => setSetupModalVisible(false)}
                style={viewStyles.modalCloseButton}
              >
                <Ionicons name="close" size={24} color={tokens.color.onSurface} />
              </Pressable>
            </View>
            <ScrollView style={viewStyles.modalBody}>
              <PropertyTypeSelector
                value={selectedPropertyType}
                onSelect={setSelectedPropertyType}
              />
              <View style={{ height: utils.spacing.xl }} />
              <ModuleSelector
                selectedModules={safeSelectedModules}
                onToggle={(module) => {
                  if (safeSelectedModules.includes(module)) {
                    setSelectedModules(safeSelectedModules.filter(m => m !== module));
                  } else {
                    setSelectedModules([...safeSelectedModules, module]);
                  }
                }}
                propertyType={selectedPropertyType}
              />
            </ScrollView>
            <View style={viewStyles.modalFooter}>
              <Button
                title="Cancel"
                onPress={() => setSetupModalVisible(false)}
                variant="outline"
                style={{ flex: 1 }}
              />
              <Button
                title="Initialize"
                onPress={handleInitialize}
                variant="primary"
                loading={initializing}
                style={{ flex: 1 }}
                disabled={safeSelectedModules.length === 0}
              />
            </View>
          </View>
        </View>
      </Modal>

      {/* Decision Modal */}
      <Modal
        visible={decisionModalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setDecisionModalVisible(false)}
      >
        <View style={viewStyles.modalOverlay}>
          <View style={viewStyles.modalContent}>
            <View style={viewStyles.modalHeader}>
              <Text style={viewStyles.modalTitle}>Make Decision</Text>
              <Pressable
                onPress={() => setDecisionModalVisible(false)}
                style={viewStyles.modalCloseButton}
              >
                <Ionicons name="close" size={24} color={tokens.color.onSurface} />
              </Pressable>
            </View>
            <ScrollView style={viewStyles.modalBody}>
              <Text style={viewStyles.decisionModalLabel}>Decision</Text>
              <View style={viewStyles.decisionButtons}>
                {(['go', 'no_go', 'pass_for_now'] as FinalDecision[]).map((dec) => (
                  <Pressable
                    key={dec}
                    onPress={() => setDecision(dec)}
                    style={({ pressed }) => [
                      viewStyles.decisionButton,
                      decision === dec && viewStyles.decisionButtonSelected,
                      pressed && viewStyles.decisionButtonPressed,
                    ]}
                  >
                    <Text style={[
                      viewStyles.decisionButtonText,
                      decision === dec && viewStyles.decisionButtonTextSelected,
                    ]}>
                      {dec === 'go' ? 'Go' : dec === 'no_go' ? 'No Go' : 'Pass for Now'}
                    </Text>
                  </Pressable>
                ))}
              </View>
              <Text style={viewStyles.decisionModalLabel}>Rationale (Optional)</Text>
              <TextInput
                style={viewStyles.decisionRationaleInput}
                value={decisionRationale}
                onChangeText={setDecisionRationale}
                placeholder="Explain your decision..."
                placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.5)}
                multiline
                textAlignVertical="top"
              />
            </ScrollView>
            <View style={viewStyles.modalFooter}>
              <Button
                title="Cancel"
                onPress={() => {
                  setDecision(null);
                  setDecisionRationale('');
                  setDecisionModalVisible(false);
                }}
                variant="outline"
                style={{ flex: 1 }}
              />
              <Button
                title="Save Decision"
                onPress={handleSaveDecision}
                variant="primary"
                loading={saving}
                style={{ flex: 1 }}
                disabled={!decision}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

function makeViewStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  isSmallMobile: boolean
) {
  return StyleSheet.create({
    container: {
      width: '100%',
    },
    loadingContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: utils.spacing['2xl'],
    },
    loadingText: {
      fontSize: utils.typography.sizes.base,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      marginTop: utils.spacing.md,
      fontFamily: utils.typography.body.fontFamily,
    },
    emptyState: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: utils.spacing['2xl'],
    },
    emptyText: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      marginTop: utils.spacing.md,
      fontFamily: utils.typography.heading.fontFamily,
    },
    emptySubtext: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      marginTop: utils.spacing.xs,
      textAlign: 'center',
      fontFamily: utils.typography.body.fontFamily,
    },
    summaryRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: isSmallMobile ? utils.spacing.xs : utils.spacing.sm,
      marginBottom: utils.spacing.lg,
    },
    summaryCard: {
      flex: 0,
      minWidth: isSmallMobile ? '30%' : isMobile ? '30%' : 100,
      maxWidth: isSmallMobile ? '32%' : isMobile ? '32%' : 120,
      alignItems: 'center',
      paddingHorizontal: isSmallMobile ? utils.spacing.xs : utils.spacing.sm,
      paddingVertical: isSmallMobile ? utils.spacing.sm : utils.spacing.md,
    },
    summaryLabel: {
      fontSize: isSmallMobile ? 9 : utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
      textTransform: 'uppercase',
      letterSpacing: 0.3,
      textAlign: 'center',
    },
    summaryValue: {
      fontSize: isSmallMobile ? utils.typography.sizes.lg : utils.typography.sizes.xl,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
    },
    actionsRow: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: utils.spacing.md,
      marginBottom: utils.spacing.lg,
    },
    decisionCard: {
      gap: utils.spacing.sm,
    },
    decisionLabel: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      fontFamily: utils.typography.body.fontFamily,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    decisionContent: {
      gap: utils.spacing.xs,
    },
    decisionValue: {
      fontSize: utils.typography.sizes.xl,
      fontWeight: utils.typography.weights.bold,
      fontFamily: utils.typography.heading.fontFamily,
    },
    decisionRationale: {
      fontSize: utils.typography.sizes.base,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      marginTop: utils.spacing.xs,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    modalContent: {
      backgroundColor: tokens.color.surface,
      borderTopLeftRadius: utils.radius.xl,
      borderTopRightRadius: utils.radius.xl,
      maxHeight: '90%',
      ...Platform.select({
        web: {
          borderTopLeftRadius: utils.radius.lg,
          borderTopRightRadius: utils.radius.lg,
        } as any,
      }),
    },
    modalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: utils.spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    modalTitle: {
      fontSize: utils.typography.sizes.xl,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
    },
    modalCloseButton: {
      padding: utils.spacing.xs,
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    modalBody: {
      padding: utils.spacing.lg,
      maxHeight: 500,
    },
    modalFooter: {
      flexDirection: 'row',
      gap: utils.spacing.md,
      padding: utils.spacing.lg,
      borderTopWidth: 1,
      borderTopColor: tokens.color.border,
    },
    decisionModalLabel: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.sm,
      fontFamily: utils.typography.body.fontFamily,
    },
    decisionButtons: {
      flexDirection: 'row',
      gap: utils.spacing.md,
      marginBottom: utils.spacing.lg,
      flexWrap: 'wrap',
    },
    decisionButton: {
      flex: isMobile ? 1 : 0,
      minWidth: isMobile ? '48%' : 120,
      padding: utils.spacing.md,
      borderRadius: utils.radius.md,
      backgroundColor: tokens.color.surface,
      borderWidth: 2,
      borderColor: tokens.color.border,
      alignItems: 'center',
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    decisionButtonSelected: {
      borderColor: tokens.color.primary,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
    },
    decisionButtonPressed: {
      opacity: 0.7,
    },
    decisionButtonText: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    decisionButtonTextSelected: {
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.semibold,
    },
    decisionRationaleInput: {
      minHeight: 120,
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.md,
      padding: utils.spacing.md,
      backgroundColor: tokens.color.surface,
      color: tokens.color.onSurface,
      fontSize: utils.typography.sizes.base,
      fontFamily: utils.typography.body.fontFamily,
      ...Platform.select({
        web: {
          outline: 'none',
        } as any,
      }),
    },
  });
}

